/**
 * Shared safety boundary for anything we ship out of the app — error telemetry
 * today, LLM page context next. Two jobs: redact secrets by key name, and cap
 * size so a deep Redux slice can never blow up the payload.
 *
 * Limits are per-sanitizer, not global: telemetry wants generous breadcrumbs,
 * a prompt context wants far tighter ones.
 */

export type SanitizeLimits = {
  maxKeysPerObject: number
  maxArrayItems: number
  maxStringChars: number
}

export const DEFAULT_SANITIZE_LIMITS: SanitizeLimits = {
  maxKeysPerObject: 60,
  maxArrayItems: 40,
  maxStringChars: 300,
}

export const REDACT_KEY_RE =
  /(token|password|secret|authorization|jwt|cookie|session|csrf|license|api[_-]?key)/i

export const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' &&
  v !== null &&
  Object.getPrototypeOf(v) === Object.prototype

export type Sanitizer = (
  value: unknown,
  depth: number,
  keyHint?: string
) => unknown

export const createSanitizer = (
  overrides: Partial<SanitizeLimits> = {}
): Sanitizer => {
  const limits = { ...DEFAULT_SANITIZE_LIMITS, ...overrides }

  const clampString = (s: string): string =>
    s.length > limits.maxStringChars
      ? `${s.slice(0, limits.maxStringChars)}…`
      : s

  const sanitize: Sanitizer = (value, depth, keyHint) => {
    if (keyHint && REDACT_KEY_RE.test(keyHint)) return '[redacted]'
    if (depth <= 0) return '[truncated]'

    if (
      value === null ||
      value === undefined ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    )
      return value

    if (typeof value === 'string') return clampString(value)

    if (value instanceof Date) return value.toISOString()

    if (value instanceof Error) {
      return {
        name: value.name,
        message: clampString(value.message),
      }
    }

    if (Array.isArray(value)) {
      return value
        .slice(0, limits.maxArrayItems)
        .map(v => sanitize(v, depth - 1))
    }

    if (isPlainObject(value)) {
      const out: Record<string, unknown> = {}
      const entries = Object.entries(value).slice(0, limits.maxKeysPerObject)
      for (const [k, v] of entries) {
        out[k] = sanitize(v, depth - 1, k)
      }
      return out
    }

    // Functions, class instances, bigints, symbols, etc.
    try {
      return clampString(String(value))
    } catch {
      return '[unserializable]'
    }
  }

  return sanitize
}

/** Sanitizer at the default limits. */
export const sanitizeValue = createSanitizer()

/**
 * Drop what a payload does not know: null, undefined, and containers left empty
 * once their own empties are gone. Returns `undefined` when nothing survives.
 *
 * Producers can then declare a full shape for type safety while the wire payload
 * carries only populated fields. Shipping `cloud: null, filters: null,
 * visible: null` costs tokens every turn and invites a model to reason about
 * things that are simply absent — an omitted key says "unknown" more clearly
 * than an explicit null.
 *
 * `false`, `0` and `''` survive: those are answers, not absences.
 */
export const pruneEmpty = (value: unknown): unknown => {
  if (value === null || value === undefined) return undefined

  if (Array.isArray(value)) {
    const items = value.map(pruneEmpty).filter(item => item !== undefined)
    return items.length ? items : undefined
  }

  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) {
      const pruned = pruneEmpty(item)
      if (pruned !== undefined) out[key] = pruned
    }
    return Object.keys(out).length ? out : undefined
  }

  return value
}
